import { Dispatch } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { Product } from '~/types/products';
import { ReduxState } from '~/types/redux';
import {
  ModalActionTypes,
  ModalActions,
  SetActiveModalAction,
  SetModalProductAction,
  SetModalDataAction,
  ClearModalAction,
  SetModalPriceProductAction,
} from '~/types/modal';
import { AddOnPriceSimplifiedProps } from '~/components/AddOn/simplified/AddOnPriceSimplified';

export const setActiveModal = (modalKey: string | null): SetActiveModalAction => ({
  type: ModalActionTypes.SET_ACTIVE_MODAL,
  modalKey,
});

export const setModalProduct = (product: Product): SetModalProductAction => ({
  type: ModalActionTypes.SET_MODAL_PRODUCT,
  product,
});

export const setModalPriceProduct = (priceProduct: AddOnPriceSimplifiedProps): SetModalPriceProductAction => ({
  type: ModalActionTypes.SET_MODAL_PRICE_PRODUCT,
  priceProduct,
});

export const setModalData = (data: Record<string, unknown>): SetModalDataAction => ({
  type: ModalActionTypes.SET_MODAL_DATA,
  data,
});

export const clearModal = (): ClearModalAction => ({
  type: ModalActionTypes.CLEAR_MODAL,
});

// Main modal actions with safe behavior (prevents opening when modal already active)
export const openModal =
  (modalKey: string): ThunkAction<void, ReduxState, unknown, ModalActions> =>
  (dispatch, getState) => {
    const state = getState();
    const currentActiveModal = state.modal?.activeModal ?? null;

    // Only open modal if no modal is currently active
    if (currentActiveModal === null) {
      dispatch(setActiveModal(modalKey));
    }
  };

export const openModalWithProduct =
  (
    modalKey: string,
    product?: Product,
    priceProduct?: AddOnPriceSimplifiedProps
  ): ThunkAction<void, ReduxState, unknown, ModalActions> =>
  (dispatch, getState) => {
    const state = getState();
    const currentActiveModal = state.modal?.activeModal ?? null;

    // Only open modal if no modal is currently active
    if (currentActiveModal === null) {
      product && dispatch(setModalProduct(product));
      priceProduct && dispatch(setModalPriceProduct(priceProduct));
      dispatch(setActiveModal(modalKey));
    }
  };

export const openModalWithData =
  (modalKey: string, data: Record<string, unknown>): ThunkAction<void, ReduxState, unknown, ModalActions> =>
  (dispatch, getState) => {
    const state = getState();
    const currentActiveModal = state.modal?.activeModal ?? null;

    // Only open modal if no modal is currently active
    if (currentActiveModal === null) {
      dispatch(setModalData(data));
      dispatch(setActiveModal(modalKey));
    }
  };

export const closeModal = () => (dispatch: Dispatch<ModalActions>) => {
  dispatch(clearModal());
};

// Direct modal actions (bypass safety checks)
export const setActiveModalDirect = (modalKey: string) => (dispatch: Dispatch<ModalActions>) => {
  dispatch(setActiveModal(modalKey));
};

export const replaceModal = (modalKey: string) => (dispatch: Dispatch<ModalActions>) => {
  // Force modal replacement regardless of current state
  dispatch(setActiveModal(modalKey));
};

export const openModalWithProductDirect =
  (modalKey: string, product: Product) => (dispatch: Dispatch<ModalActions>) => {
    dispatch(setModalProduct(product));
    dispatch(setActiveModal(modalKey));
  };

export const openModalWithDataDirect =
  (modalKey: string, data: Record<string, unknown>) => (dispatch: Dispatch<ModalActions>) => {
    dispatch(setModalData(data));
    dispatch(setActiveModal(modalKey));
  };
